import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Mail,
  MessageSquare,
  HelpCircle,
  Plug,
  CreditCard,
  Bug,
  Loader2,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "general", label: "General question", icon: HelpCircle, color: "text-cyan-500" },
  { id: "clover", label: "Clover connection", icon: Plug, color: "text-blue-500" },
  { id: "billing", label: "Billing & accounts", icon: CreditCard, color: "text-emerald-500" },
  { id: "bug", label: "Found a bug", icon: Bug, color: "text-red-500" },
];

const FAQS = [
  {
    q: "How do I connect my Clover register?",
    a: "Sign in to your dashboard, open your Grocery view, and click the big \"Connect Clover\" banner. You'll be redirected to clover.com to log in with your existing Clover merchant account, click \"Allow\", and you're back in our app with your live data flowing in. The whole flow takes under 60 seconds.",
  },
  {
    q: "Will you charge my card?",
    a: "No. The platform is currently free for everyone. If we ever introduce paid tiers, you'll get advance notice and your existing data will continue to work — we won't lock anyone out without warning.",
  },
  {
    q: "Can you read or change my data on Clover?",
    a: "We can only READ your sales, payments, inventory, employees, and customers. We never modify, refund, or write to your Clover account. You can revoke our access any time from your Clover dashboard or by clicking \"Disconnect\" on our app.",
  },
  {
    q: "How do I reset my password?",
    a: "Click \"Forgot password?\" on the login page. We'll email you a reset link that's valid for one hour.",
  },
  {
    q: "I never got my verification email after signing up.",
    a: "Check your spam folder first. If it's not there, go back to /signup or login and click \"Resend\" — we'll issue a fresh link. If that still doesn't work, send us a message below.",
  },
  {
    q: "Can I export my data?",
    a: "Yes. Tax reports, sales reports, and inventory all support CSV / PDF export. For a full data dump, reach out via the form below and we'll send you a complete archive.",
  },
];

export default function Support() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    subject: "",
    message: "",
    category: "general",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please describe your issue in at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/contact`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <Card className={`w-full max-w-md ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
          <CardContent className="p-8 text-center space-y-5" data-testid="support-success-card">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Message received</h2>
            <p className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              Thanks for reaching out. We respond to every ticket within <span className="font-semibold">1 business day</span>. Keep an eye on{" "}
              <span className="font-mono">{form.email}</span>.
            </p>
            <div className="flex flex-col gap-2">
              <Link to="/welcome">
                <Button className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white" data-testid="support-success-home">Back to home</Button>
              </Link>
              <Button
                variant="ghost"
                onClick={() => { setSubmitted(false); setForm({ ...form, subject: "", message: "" }); }}
                className={isDark ? 'text-slate-400' : 'text-slate-500'}
                data-testid="support-success-another"
              >
                Send another message
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'}`} data-testid="support-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/welcome" className={`inline-flex items-center gap-2 text-sm mb-8 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`} data-testid="support-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Support</h1>
          <p className={`text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            We respond to every message within 1 business day. Below are the most common questions — or scroll down to send us a note.
          </p>
        </div>

        {/* FAQ */}
        <div className="grid gap-3 mb-12">
          {FAQS.map((f, i) => (
            <Card
              key={i}
              className={`cursor-pointer transition-colors ${isDark ? 'bg-slate-900 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200 hover:border-slate-300'}`}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              data-testid={`faq-${i}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <h3 className={`font-semibold text-[15px] ${isDark ? 'text-white' : 'text-slate-900'}`}>{f.q}</h3>
                  <span className={`text-xl leading-none ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{openFaq === i ? '−' : '+'}</span>
                </div>
                {openFaq === i && (
                  <p className={`text-sm mt-3 leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{f.a}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact form */}
        <Card className={`${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Send us a message</h2>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Replies go straight to the inbox you list below.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Category */}
              <div>
                <Label className={`text-sm mb-2 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>What can we help with?</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const selected = form.category === c.id;
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setForm({ ...form, category: c.id })}
                        className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border text-xs font-medium transition-all ${
                          selected
                            ? (isDark ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-cyan-500 bg-cyan-50 text-cyan-700')
                            : (isDark ? 'border-slate-700 text-slate-400 hover:border-slate-600' : 'border-slate-200 text-slate-600 hover:border-slate-300')
                        }`}
                        data-testid={`support-category-${c.id}`}
                      >
                        <Icon className={`w-4 h-4 ${selected ? '' : c.color}`} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Your name</Label>
                  <Input
                    id="name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={`mt-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                    data-testid="support-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={`mt-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                    data-testid="support-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="business" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Business name <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  id="business"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                  data-testid="support-business-input"
                />
              </div>

              <div>
                <Label htmlFor="subject" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Subject</Label>
                <Input
                  id="subject"
                  required
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Quick summary of the issue"
                  className={`mt-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                  data-testid="support-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="message" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Message</Label>
                <Textarea
                  id="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What's going on? Include any error messages, the steps to reproduce, and which business you're on."
                  className={`mt-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                  data-testid="support-message-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'} flex items-center gap-1`}>
                  <Mail className="w-3.5 h-3.5" /> Or email us directly: <a href="mailto:support@mybusinessportal.info" className="underline">support@mybusinessportal.info</a>
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white sm:px-8"
                  data-testid="support-submit-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send message"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
